import NUIAlert from '@/components/Alert/Alert'
import { IAppStore } from '@/models/store.model'
import { webApiService } from '@/services'
import { blobToBase64 } from '@/utils/blobToBase64.util'
import { Box, Button as MuiButton, CircularProgress, TextField } from '@mui/material'
import { Button, Chip, Spinner } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import React, { useRef, useState } from 'react'
import { MdOpenInNew, MdPhotoCamera, MdRefresh } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTradeinContext } from '../hooks/useTradeinContext'

/* Enlaces de ayuda de cada fabricante para ubicar el número de serie de la consola. */
const HELP_LINKS = [
  { key: 'sony', label: 'PlayStation', match: ['sony', 'playstation', 'ps4', 'ps5'], url: 'https://www.playstation.com/es-mx/support/hardware/ps4-find-serial-and-model-numbers/' },
  { key: 'nintendo', label: 'Nintendo', match: ['nintendo', 'switch'], url: 'https://es-americas-support.nintendo.com/app/answers/detail/a_id/58880/~/%C2%BFd%C3%B3nde-puedo-encontrar-el-n%C3%BAmero-de-serie-de-mi-consola%3F' }
]

interface IOwnProps {
  /* Guarda la serie. suggested es lo que devolvió Gemini, para poder auditar cuánto se corrige. */
  onSave: (serie: string, suggested: string | null) => void
  saving: boolean
}

const SerieConsole: React.FC<IOwnProps> = ({ onSave, saving }) => {
  const { ctx, saveCtx } = useTradeinContext()
  const userState = useSelector((store: IAppStore) => store.user)
  const { slug } = useParams()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string>('')
  const [reading, setReading] = useState(false)
  const [suggested, setSuggested] = useState<string | null>(null)
  const [readDone, setReadDone] = useState(false)
  const [readMessage, setReadMessage] = useState('')

  const brand = String(ctx.tradein.brand ?? '').toLowerCase()
  const model = String(ctx.tradein.model ?? '').toLowerCase()
  const brandLinks = HELP_LINKS.filter(link => link.match.some(word => brand.includes(word) || model.includes(word)))
  // Si no se reconoce la marca se ofrecen los dos enlaces.
  const links = brandLinks.length > 0 ? brandLinks : HELP_LINKS

  /* La lectura es una ayuda: si falla o no detecta nada, la serie se escribe a mano. */
  const readSerie = async (imageFile: File) => {
    setReading(true)
    setReadDone(false)
    try {
      const base64 = await blobToBase64(imageFile)
      const res = await webApiService.readSerieFromImage({
        id_tradein: ctx.tradein.id,
        seller: slug,
        store_user: String(userState.id),
        sbu: String(ctx.tradein.sbu),
        brand: String(ctx.tradein.brand),
        model: String(ctx.tradein.model),
        file: { name: imageFile.name, base64: String(base64) }
      })
      // La serie viene dentro de data; message es el motivo para mostrar al vendedor
      // (comentario es el detalle técnico de la IA, no se muestra en pantalla).
      const serial = res?.data?.serie ? String(res.data.serie).trim() : ''
      setReadMessage(String(res?.message ?? '').trim())
      if (serial !== '') {
        setSuggested(serial)
        saveCtx({ ...ctx, serie: serial })
      } else {
        setSuggested(null)
      }
    } catch (error: any) {
      // Nunca se bloquea el flujo: se deja el aviso y la serie se ingresa manualmente.
      console.log('🚀 ~ readSerie ~ error:', error)
      setSuggested(null)
      setReadMessage('')
    } finally {
      setReading(false)
      setReadDone(true)
    }
  }

  /* Se limpia el value para que al elegir la misma foto igual se dispare onChange. */
  const openFilePicker = () => {
    if (fileInputRef.current === null) return
    fileInputRef.current.value = ''
    fileInputRef.current.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    if (!selected) return
    try {
      const imageCompression = (await import('browser-image-compression')).default
      // Se comprime menos que en el resto de la app: la serie es texto chico y hay que poder leerla.
      const compressed = await imageCompression(selected, {
        maxSizeMB: 1.5,
        maxWidthOrHeight: 1600,
        useWebWorker: true
      })
      const compressedFile = compressed as File
      // Al cambiar de foto se descarta la lectura anterior; si la serie venía de esa lectura y no
      // la corrigieron, se limpia para no guardar la serie de una foto que ya no está.
      if (suggested !== null && suggested === String(ctx.serie ?? '')) saveCtx({ ...ctx, serie: '' })
      setSuggested(null)
      setReadMessage('')
      setReadDone(false)
      if (preview !== '') URL.revokeObjectURL(preview)
      setFile(compressedFile)
      setPreview(URL.createObjectURL(compressedFile))
      readSerie(compressedFile)
    } catch (error: any) {
      console.log('🚀 ~ handleFileChange ~ error:', error)
      enqueueSnackbar('No fue posible procesar la imagen.', { variant: 'error' })
    }
  }

  const removeFile = () => {
    if (preview !== '') URL.revokeObjectURL(preview)
    setFile(null)
    setPreview('')
    setSuggested(null)
    setReadDone(false)
    setReadMessage('')
    if (fileInputRef.current !== null) fileInputRef.current.value = ''
  }

  const serie = String(ctx.serie ?? '')

  return (
    <div className='mt-2'>
      <div className='flex flex-wrap items-center gap-2 mb-2'>
        {links.map(link => (
          <Button
            key={link.key}
            as='a'
            href={link.url}
            target='_blank'
            rel='noreferrer'
            size='sm'
            color='primary'
            endContent={<MdOpenInNew style={{ fontSize: '13px' }} />}
          >
            {links.length > 1 ? `Cómo encontrar la serie (${link.label})` : 'Cómo encontrar la serie'}
          </Button>
        ))}
      </div>

      <NUIAlert type='info'>
        Adjunta la foto de la etiqueta con el N° de serie de la consola. Se intentará leer automáticamente,
        pero siempre puedes corregirla antes de guardar.
      </NUIAlert>

      <div
        style={{
          border: file !== null ? '1px solid #ccc' : '1px dashed #ff8181',
          borderRadius: '5px',
          padding: '20px',
          textAlign: 'center',
          marginTop: '6px',
          backgroundColor: file !== null ? '#fff' : '#ffefef',
          color: '#000000'
        }}
      >
        <input
          type='file'
          accept='image/png, image/jpg, image/jpeg'
          id='file-input-serie-consola'
          className='hidden'
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        {file === null
          ? (
            <>
              <Button size='sm' color='warning' onPress={openFilePicker}>
                Adjuntar foto del N° de serie (Requerido)
              </Button>
              <p>(Solo se permiten archivos .png, .jpg y .jpeg)</p>
            </>
            )
          : (
            <div className='flex flex-col items-center gap-2'>
              <img src={preview} alt='Foto del número de serie' className='max-w-full md:max-w-md rounded-md' />
              <p className='text-xs'>{file.name}</p>
              {/* Mientras no se guarda la serie se puede reemplazar la foto todas las veces que sea necesario. */}
              <div className='flex flex-wrap items-center justify-center gap-2'>
                <Button size='sm' color='warning' variant='flat' startContent={<MdPhotoCamera />} onPress={openFilePicker} isDisabled={reading || saving}>
                  Cambiar foto
                </Button>
                <Button size='sm' variant='flat' startContent={<MdRefresh />} onPress={() => readSerie(file)} isDisabled={reading || saving}>
                  Volver a leer
                </Button>
                <Button color='danger' size='sm' onPress={removeFile} isDisabled={reading || saving}>
                  Eliminar
                </Button>
              </div>
            </div>
            )}
      </div>

      {reading && (
        <div className='flex items-center gap-2 mt-2'>
          <Spinner size='sm' />
          <span className='text-sm'>Leyendo el número de serie de la foto...</span>
        </div>
      )}

      {readDone && !reading && suggested !== null && (
        <div className='mt-2'>
          <Chip color='warning' variant='flat' size='sm'>Detectado automáticamente, verifica la serie antes de guardar</Chip>
        </div>
      )}

      {readDone && !reading && suggested === null && (
        <div className='mt-2'>
          <NUIAlert type='warning'>
            {readMessage !== '' ? readMessage : 'No se pudo leer la serie de la foto. Ingrésala manualmente.'}
          </NUIAlert>
        </div>
      )}

      <Box position='relative' display='flex' alignItems='center' mt={2} mb={2}>
        <Box position='relative' flexGrow={1} mr={2}>
          <TextField
            className='search-box'
            label='Agregar serie'
            variant='outlined'
            size='small'
            value={serie}
            autoComplete='off'
            onChange={(event) => saveCtx({ ...ctx, serie: event.target.value })}
            helperText={suggested !== null && suggested !== serie ? `Lectura automática: ${suggested}` : undefined}
            style={{ width: '100%' }}
          />
        </Box>
        <MuiButton
          variant='contained'
          size='small'
          color='primary'
          disabled={saving || file === null || reading || serie.trim() === ''}
          onClick={() => onSave(serie.trim(), suggested)}
        >
          {saving ? <CircularProgress size={18} color='inherit' /> : 'Guardar'}
        </MuiButton>
      </Box>
    </div>
  )
}

export default SerieConsole
